import { JSX, ReactNode } from 'react';
import { Navigate, To } from 'react-router';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { RouteConfig } from '~/routes/routeConfig';
import { FeatureFlag, UserRole } from '~/types/company';

interface UserFeaturesGuardProps {
  children: ReactNode;
  requiredFeatures: FeatureFlag[];
  whitelistedRoles?: UserRole[];
  redirectTo?: To;
}

const redirectToDefault = (redirectTo?: To): JSX.Element => {
  return <Navigate replace to={redirectTo ?? RouteConfig.AppRoot.buildLink()} />;
};

/**
 * Guard that checks if the user has the required features to access the page.
 *
 * @param children - The content to render if access is granted
 * @param requiredFeatures - The features required to access the page
 * @param whitelistedRoles - Optional whitelist of user roles that can bypass user-level feature checks.
 *                           If specified and the user's role is in the list, access is granted after company feature validation.
 *                           If specified and the user's role is NOT in the list, normal feature validation applies.
 *                           If not specified, all roles go through normal feature validation.
 * @param redirectTo - Optional redirect path if feature validation fails (defaults to app root)
 * @constructor
 */
const UserFeaturesGuard: React.FC<UserFeaturesGuardProps> = ({
  children,
  requiredFeatures,
  whitelistedRoles,
  redirectTo,
}) => {
  const hasRequiredFeatures = useFeatureFlag(requiredFeatures, whitelistedRoles);

  return hasRequiredFeatures ? children : redirectToDefault(redirectTo);
};

export default UserFeaturesGuard;
